import React from 'react'
import { Component } from '../types/component'

interface HtmlElementProps {
  component: Component
  style: React.CSSProperties
  children?: React.ReactNode
}

/**
 * HtmlElement component that renders parsed HTML elements
 * while preserving their original tag names and attributes
 */
export function HtmlElement({ component, style, children }: HtmlElementProps) {
  const config = component.config as Record<string, unknown>
  const tagName = config.tagName as string
  const textContent = config.textContent as string | undefined
  const attributes = (config.attributes as Record<string, string>) || {}

  // Handle text nodes
  if (tagName === 'text') {
    return <>{textContent}</>
  }

  // Filter out any CSS properties that may have been incorrectly parsed as attributes
  const validAttributes: Record<string, string> = {}
  const cssPropertyNames = [
    'backgroundColor', 'fontSize', 'fontFamily', 'fontWeight', 'lineHeight',
    'color', 'padding', 'margin', 'border', 'borderRadius', 'boxShadow',
    'textAlign', 'display', 'position', 'width', 'height', 'maxWidth', 'minHeight',
    'background-color', 'font-size', 'font-family', 'font-weight', 'line-height',
    'border-radius', 'box-shadow', 'text-align', 'max-width', 'min-height'
  ]

  for (const [key, value] of Object.entries(attributes)) {
    // Skip CSS properties and invalid attributes
    if (!cssPropertyNames.includes(key) &&
        !cssPropertyNames.includes(key.replace(/:$/, '')) && // Remove trailing colon
        !/^[#(),\d]/.test(key) && // Skip values starting with special chars
        !/[(),{}[\]]/.test(key)) { // Skip values with parens/brackets
      validAttributes[key] = value
    }
  }

  // Create props object with all preserved attributes
  const props: Record<string, any> = {
    style,
    ...validAttributes,
  }

  // For className, combine any existing class attribute with hover styles
  if (validAttributes.class) {
    props.className = validAttributes.class
    delete props.class // Remove 'class' in favor of 'className'
  }

  // Render the element with the original tag name
  const Element = tagName as keyof JSX.IntrinsicElements

  return (
    <Element {...props}>
      {textContent || children}
    </Element>
  )
}
